import { DeviceData } from "./DeviceData";
import { PSF_ProtectiveFunctionComments } from "./PSF_ProtectiveFunctionComments";
import { PtType } from "./PtType";
import { PT_ProtectiveFunction } from "./PT_ProtectiveFunction";
import { MasterPoolMismatchItem } from "./MasterPoolMismatchItem";
import { isMooNVotingType, lockedVotingDevices } from "./../helpers";

type ConfigType = PtType.InputConfig | PtType.ActionConfig;

type MessageType = 'error' | 'warning' | 'readonly';

function getColumn(device: PT_ProtectiveFunction) {
  if (!device || !device.devicePosition) {
    return null;
  }
  const match = device.devicePosition.trim().match(/^D([1-9])([1-9])$/);
  return match ? match[1] : null;
}

function getRow(device: PT_ProtectiveFunction) {
  if (!device || !device.devicePosition) {
    return null;
  }
  const match = device.devicePosition.trim().match(/^D([1-9])([1-9])$/);
  return match ? match[2] : null;
}

function isManual(device: PT_ProtectiveFunction) {
  const manual = [PtType.ManualAction, PtType.ManualInput, PtType.ManualInputConfig];
  return manual.indexOf(device.ptType) != -1;
}

function isDevice(device: PT_ProtectiveFunction) {
  const manual = [PtType.ActionDevice, PtType.InputDevice, PtType.SupportDevice];
  return manual.indexOf(device.ptType) != -1;
}

function isInputOrAction(device: PT_ProtectiveFunction) {
  const manual = [PtType.ActionDevice, PtType.InputDevice];
  return manual.indexOf(device.ptType) != -1;
}

function getDisplayText(type: PtType, slot: number) {
  switch (type) {
    case PtType.ActionConfig:
    case PtType.ActionDevice: {
      return `Action${slot}`;
    }
    case PtType.InputConfig:
    case PtType.InputDevice: {
      return `Input${slot}`;
    }
    case PtType.SupportDevice: {
      return `Support${slot}`;
    }
    case PtType.LogicSolver: {
      return `LogicSolver`;
    }
    case PtType.ManualInputConfig:
    case PtType.ManualInput: {
      return `Manual Input ${slot}`;
    }
    case PtType.ManualAction: {
      return `Manual Action`;
    }

    default: {
      return null;
    }
  }
}

interface MessageItem {
    id: string | number;
    text: string;
}

export class Message {
    message: string;
    messageType: MessageType;
    messageItems?: MessageItem[];
    messageTemplateType: MessageTemplateType;
    constructor(message: string, messageType: MessageType, messageItems?: MessageItem[], messageTemplateType?: MessageTemplateType) {
        this.message = message;
        this.messageType = messageType;

        this.messageItems = messageItems;
        this.messageTemplateType = messageTemplateType;
        this.message = this.formatMessage();
    }

    //useId - set to true, when you need to display single message template with dynamic 'id' value instead of 'text'
    formatMessageById(id: number | string, useId: boolean = false): string {
        var result = "";
        if (!this.messageItems)
            return result;

        const message = this.messageItems.find(x => x.id === id);
        if (message) {
            if(useId) {
                result = this.getMessageTemplate(this.messageTemplateType, message.id.toString(), false);
            }
            else {
                result = this.getMessageTemplate(this.messageTemplateType, message.text, false);
            }
        }

        return result;
    } 

    formatMessage(): string {
        var result = "";

        if (!this.messageItems) 
            return result;

        const plural = this.messageItems.length > 1;
        result = this.getMessageTemplate(this.messageTemplateType, this.message, plural);
        return result;
    }

    private getMessageTemplate(type: MessageTemplateType, message: string, plural: boolean) {
        switch (type) {
            case MessageTemplateType.EnsureOverhaulAndProofTestCoverage: {
                if (plural) {
                    return `Devices on ${message} have a proof test coverage less than 100 but an overhaul interval of 0.`;
                } else {
                    return `Device on ${message} has a proof test coverage less than 100 but an overhaul interval of 0.`;
                }
            }
            case MessageTemplateType.EnsureProofTestCoverageMinMaxValue: {
                if (plural) {
                    return `Devices on ${message} have a proof test coverage less than 0 or greater than 100.`;
                } else {
                    return `Device on ${message} has a proof test coverage less than 0 or greater than 100.`;
                }
            }
            case MessageTemplateType.EnsureDiagnosticInterval: {
                if (plural) {
                    return `Devices on ${message} have a diagnostic level without a diagnostic interval.`;
                } else {
                    return `Device on ${message} has a diagnostic level without a diagnostic interval.`;
                }
            }
            case MessageTemplateType.EnsureVoting: {
                if (plural) {
                    return `Devices on ${message} have an empty voting`;
                } else {
                    return `Device on ${message} has an empty voting`;
                }
            }
            case MessageTemplateType.EnsureTestInterval:
                return `Device${plural ? 's' : ''} on ${message} ha${plural ? 've' : 's'} no proof test interval.`;
            case MessageTemplateType.EnsureNoBlankChannels: 
                return `${message} ha${plural ? 've' : 's'} an empty channel.`;
            case MessageTemplateType.EnsureRedundantConfigurationsHaveCCF:
                return `Missing common cause for ${message}.`;
            case MessageTemplateType.MasterPoolPropertyMismatch:
            case MessageTemplateType.EnsureLogicSolverHasVoting:
            case MessageTemplateType.ManualElements:
            case MessageTemplateType.Custom:
                return this.message;
            default:
                return "";
        }
    }
}

export enum MessageTemplateType {
    EnsureTestInterval,
    EnsureOverhaulAndProofTestCoverage,
    EnsureNoBlankChannels,
    EnsureDiagnosticInterval,
    EnsureLogicSolverHasVoting,
    EnsureRedundantConfigurationsHaveCCF,
    ManualElements,
    Custom,    
    MasterPoolPropertyMismatch,
    EnsureProofTestCoverageMinMaxValue,
    EnsureVoting
}

class ConfigSlotWrapper {
  config: string;
  ccf: number;
  configType: ConfigType;
  slot: number;

  constructor(config: string, ccf: number, configType: ConfigType, slot: number) {
    this.config = config;
    this.ccf = ccf;
    this.configType = configType;
    this.slot = slot;
  }

  get hasConfig(): boolean {
    return this.config ? true : false;
  }

  get displayText() {
    return getDisplayText(this.configType, this.slot);
  }

  getDeviceType() {
    if (this.configType === PtType.ActionConfig) {
      return PtType.ActionDevice;
    }
    else if (this.configType == PtType.InputConfig) {
      return PtType.InputDevice;
    }
    else if (this.configType === PtType.ManualInputConfig) {
      return PtType.ManualInput;
    }
    throw "Error: Unexpected config type.";
  }

  hasChildDevice(pt: PT_ProtectiveFunction) {
    const childDeviceType = this.configType === PtType.ActionConfig ? PtType.ActionDevice : PtType.InputDevice;
    return pt.ptType === childDeviceType && pt.slot == this.slot;
  }

  getEmptyColumns(childDevices: PT_ProtectiveFunction[]) {
    const emptyColumns = [];    
    for (var i = this.numColumns; i > 0; i--) {
      if (childDevices.find(x => getColumn(x) == i.toString()) == null) {
        let columnOffset = 0; //need for dynamic column order
        if (this.configType === PtType.InputConfig) {
          if (this.isMooNConfig) {
            columnOffset = 0;
          } else {
            if (this.numColumns == 2 && i == 2) {
              columnOffset = 1;
            } else if (this.numColumns == 1) {
              columnOffset = 1;
            }
          }
        }
        emptyColumns.push((i + columnOffset).toString());
      }
    }
    return emptyColumns;
  }

  get isMooNConfig(): boolean {
    if (!this.hasConfig) {
      return false;
    }
    return isMooNVotingType(this.config);
  }

  get hasRedundantConfig(): boolean {
    if (!this.hasConfig) {
      return false;
    }
    if (this.configType === PtType.ActionConfig) {
      return this.config === 'Simplex' ? false : true;
    }
    else if (this.configType === PtType.InputConfig) {
      var match = this.config.match(/[A-Za-z]*([\d]*)oo([\d]*).*/);
      var redundant = parseInt(match[1]) < parseInt(match[2]);
      return redundant;
    }
    console.log("Unanticipated hasRedundantConfig execution " + this.config);
    return false;
  }

  get numColumns(): number {
    if (!this.hasConfig) {
      return 0;
    }
    if (this.configType === PtType.ActionConfig) {
      switch (this.config) {
        case "Simplex":
          return 1;
        case "Duplicated":
          return 2;
        case "Triplex":
          return 3;
      }
    }
    else if (this.configType === PtType.InputConfig) {
      var match = this.config.match(/[A-Za-z]*([\d]*)oo([\d]*).*/);
      return parseInt(match[2]);
    }
    console.log("Unanticipated hasRedundantConfig execution " + this.config);
    return 0;
  }

  get hasCCF(): boolean {
    return this.ccf > 0;
  }
}

interface SupportConfig {
  ss1: boolean;
  ss2: boolean;
}

interface PropertyMatch {
  name: string;
  label: string;
}

export class ProtectiveFunctionWrapper {
  deviceData: DeviceData[];
  psfComments: PSF_ProtectiveFunctionComments;
  supportConfigs: SupportConfig;
  masterPoolMismatchItems: MasterPoolMismatchItem[];

  constructor(deviceData: DeviceData[], psfComments: PSF_ProtectiveFunctionComments, supportConfigs: SupportConfig) {
    this.deviceData = deviceData;
    this.psfComments = psfComments;
    this.supportConfigs = supportConfigs;
  }

  get pts() {
    return this.deviceData.map(dd => dd.ptProtectiveFunction);
  }

  get inputConfigs() {
    const configs: ConfigSlotWrapper[] = [];
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.input1Config,
        this.psfComments.input1CCF,
        PtType.InputConfig,
        1));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.input2Config,
        this.psfComments.input2CCF,
        PtType.InputConfig,
        2));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.input3Config,
        this.psfComments.input3CCF,
        PtType.InputConfig,
        3));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.input4Config,
        this.psfComments.input4CCF,
        PtType.InputConfig,
        4));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.input5Config,
        this.psfComments.input5CCF,
        PtType.InputConfig,
        5));
    return configs;
  }

  get actionConfigs() {
    const configs: ConfigSlotWrapper[] = [];
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.action1Config,
        this.psfComments.action1CCF,
        PtType.ActionConfig,
        1));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.action2Config,
        this.psfComments.action2CCF,
        PtType.ActionConfig,
        2));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.action3Config,
        this.psfComments.action3CCF,
        PtType.ActionConfig,
        3));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.action4Config,
        this.psfComments.action4CCF,
        PtType.ActionConfig,
        4));
    configs.push(
      new ConfigSlotWrapper(
        this.psfComments.action5Config,
        this.psfComments.action5CCF,
        PtType.ActionConfig,
        5));
    return configs;
  }

  get allConfigs() {
    return this.inputConfigs.concat(this.actionConfigs);
  }

  addMasterPoolMismatchItems(items: MasterPoolMismatchItem[]) {
    this.masterPoolMismatchItems = items;
  }

  private getMismatchPropertyFullName(name: string): string {
    switch (name) {
      case "fdfrpy":
        return "Failure Dangerous Failure Rate";
      case "fsfrpy":
        return "Failure Spurious Failure Rate";
      case "ccfdm":
        return "Common Cause Factor CCF Dual";
      case "ccftm":
        return "Common Cause Factor CCF Triple";
      case "dcsm":
        return "Diagnostic Coverage Simplex DC1";
      case "dcdm":
        return "Diagnostic Coverage Dual DC2";
      case "dctm":
        return "Diagnostic Coverage Triple DC3";
      case "PFDAverage":
        return "PFDavg";
      case "STR1Year":
        return "STR";
      default:
        return "";
    }
  }

  private getPropertyMismatchMessages(): Message[] {
    const result = [];
    for (let item of this.masterPoolMismatchItems) {
      let deviceId = item.protectiveFunctionId.toString();
      let device = this.deviceData.find(x => x.ptProtectiveFunction.protectiveFunctionID == item.protectiveFunctionId);
      if (device) {
        deviceId = device.ptProtectiveFunction.deviceId;
      }
      let msg = `Device '${deviceId}' - property '${this.getMismatchPropertyFullName(item.propertyName)}' does not match master pool.`;
      result.push(new Message(
        msg,
        'warning',
        [<MessageItem>{ id: item.protectiveFunctionId, text: msg }],
        MessageTemplateType.MasterPoolPropertyMismatch
      ));
    }
    return result;
  }

  private checkInputsAndActionsConfigs(): Message[] {
    const inputConfigs = this.inputConfigs.filter(c => c.hasConfig);
    const actionConfigs = this.actionConfigs.filter(c => c.hasConfig);

    if (inputConfigs.length === 0 && actionConfigs.length === 0) {
      return [new Message(
        "Missing input and action subsystems.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    } else if (inputConfigs.length === 0) {
      return [new Message(
        "Missing input subsystem.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    } else if (actionConfigs.length === 0) {
      return [new Message(
        "Missing action subsystem.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    }
    return [];
  }

  private ensureRedundantConfigurationsHaveCCF() {
    const redundantConfigsWithoutCCF = this.allConfigs.filter(c => c.hasRedundantConfig && !c.hasCCF)
    var slots = redundantConfigsWithoutCCF.map(c => c.configType == PtType.InputConfig ? "Input" + c.slot : "Action" + c.slot);
    var slotText = slots.join(", ");
    if (redundantConfigsWithoutCCF.length > 0) {
      return [new Message(
        slotText,
        'warning',
        slots.map(c => <MessageItem>{ id: c, text: c}),
        MessageTemplateType.EnsureRedundantConfigurationsHaveCCF
      )]
    }
    else {
      return [];
    }
  }

  getPreCalcMessages(): Message[] {
    var messages = this.checkInputsAndActionsConfigs();
    messages = messages.concat(this.ensureRedundantConfigurationsHaveCCF());
    return messages;
  }

  getPostCalcMessages(): Message[] {
    return this.ensureLogicSolverHasVoting()
      .concat(this.ensureTestInterval())
      .concat(this.ensureDiagnosticInterval())
      .concat(this.ensureNoBlankChannels())
      .concat(this.ensureOverhaulAndProofTestCoverage())
      .concat(this.ensureProofTestCoverageMinMaxValue())
      .concat(this.validateManualInputs())
      .concat(this.getPropertyMismatchMessages())
      .concat(this.ensureVoting());
  }

  private validateManualInputs(): Message[] {
    const pfd = "PFDavg";
    const str = "STR";
    const ti = "Testing interval";
    const voting = "voting";
    const manualDeviceErrorProperties = [pfd, str, ti];
    function getMissingProperties(ptFunc: PT_ProtectiveFunction) {
      var missingProperties = [];
      if (ptFunc.pfdAverage === null || ptFunc.pfdAverage === undefined) {
        missingProperties.push(pfd);
      }
      if (ptFunc.pfdAverage === 0 && ptFunc.stR1Year === 0) {
        missingProperties.push(pfd);
        missingProperties.push(str);
      }
      if (ptFunc.stR1Year === null || ptFunc.stR1Year === undefined) {
        missingProperties.push(str);
      }
      if (ptFunc.proofTestingInterval === null || ptFunc.proofTestingInterval === undefined) {
        missingProperties.push(ti);
      }
      if (ptFunc.voting === null || ptFunc.voting === undefined || ptFunc.voting === "") {
        missingProperties.push(voting);
      }
      return missingProperties;
    }

    function generateMessage(ptFunc: PT_ProtectiveFunction, missingProperties: string[]) {
      var manualDeviceErrors = missingProperties.filter(value => -1 !== manualDeviceErrorProperties.indexOf(value));
        if (manualDeviceErrors.length > 0) {
          let slot = ptFunc.slot;
          if (ptFunc.ptType === PtType.ManualInput) {
            const devicePosition = ptFunc.devicePosition.trim();
            slot = parseInt(devicePosition.charAt(devicePosition.length - 1));
          }
        return [new Message(
          `${getDisplayText(ptFunc.ptType, slot)} has missing ${manualDeviceErrors.join(", ")}`,
          'warning',
          [<MessageItem>{ id: ptFunc.guiLocation, text: ptFunc.protectiveFunctionID.toString() }],
          MessageTemplateType.ManualElements
        )];
      }
      return [];
    }

    const manualDevices = this.pts.filter(pt => pt.ptType === PtType.ManualAction || pt.ptType === PtType.ManualInput);
    var messages: Message[] = [];
    for (let mi of manualDevices) {
      var missingProps = getMissingProperties(mi);
      var newMessage = generateMessage(mi, missingProps);
      console.log(newMessage);
      messages = messages.concat(newMessage);
    }
    return messages;
  }

  private ensureLogicSolverHasVoting(): Message[] {
    var logicSolvers = this.deviceData.filter(dd => dd.ptProtectiveFunction.ptType === PtType.LogicSolver);
    if (logicSolvers.length > 0 && (!logicSolvers[0].ptProtectiveFunction.voting)) { // if logic solver has no voting
      return [new Message(
        "Voting is not set on logic solver.",
        'error',
        [<MessageItem>{ id: logicSolvers[0].ptProtectiveFunction.protectiveFunctionID, text: logicSolvers[0].ptProtectiveFunction.protectiveFunctionID.toString() }],
        MessageTemplateType.EnsureLogicSolverHasVoting
      )]
    }
    return [];
  }

  private ensureVoting(): Message[] {
    const inputAndActionDevices = this.deviceData.filter(dd => isInputOrAction(dd.ptProtectiveFunction) && !dd.ptProtectiveFunction.voting);
    const devices = inputAndActionDevices.filter(dd => lockedVotingDevices.indexOf(dd.ptProtectiveFunction.deviceId) < 0);
    if (devices.length > 0) {
      const configNames = [...new Set(devices.map(d => getDisplayText(d.ptProtectiveFunction.ptType, d.ptProtectiveFunction.slot)))];
      return [new Message(
        `${configNames?.join(", ")}`,
        'error',
        devices.map(d => <MessageItem>{ id: d.ptProtectiveFunction.protectiveFunctionID, text: getDisplayText(d.ptProtectiveFunction.ptType, d.ptProtectiveFunction.slot) }),
        MessageTemplateType.EnsureVoting
      )]
    }
    return [];
  }

  private ensureDiagnosticInterval(): Message[] {
    const devices = this.deviceData.filter(device => !device.ptProtectiveFunction.isConfiguration && device.pmProtectiveFunction != null);

    const devicesWithDihButNoDC = devices.filter(device => device.pmProtectiveFunction.dih === 0
      && (device.ptProtectiveFunction.diagnosticLevel !== null && device.ptProtectiveFunction.diagnosticLevel.trim() !== "NO DC" && device.ptProtectiveFunction.diagnosticLevel.trim() !== ""));
    if (devicesWithDihButNoDC.length > 0) {
      const configNames = [... new Set(devicesWithDihButNoDC.map(device => getDisplayText(device.ptProtectiveFunction.ptType, device.ptProtectiveFunction.slot)))];
      return [new Message(
        `${configNames}`,
        'error',
        devicesWithDihButNoDC.map(device => <MessageItem>{ id: device.ptProtectiveFunction.protectiveFunctionID, text: getDisplayText(device.ptProtectiveFunction.ptType, device.ptProtectiveFunction.slot) }), MessageTemplateType.EnsureDiagnosticInterval
      )];
    }
    return [];
  }

  private ensureTestInterval(): Message[] {


    //devices with a nul or 0 test interval
    const devicesWithoutTestIntervals = this.deviceData.map(dd => dd.ptProtectiveFunction)
      .filter(pt => (isDevice(pt) || pt.ptType == PtType.LogicSolver) && !pt.proofTestingInterval && !isManual(pt))
      .map(pt => {
        return {
          configNameText: getDisplayText(pt.ptType, pt.slot),
          deviceId: pt.protectiveFunctionID
        };
      });


    var messages: Message[] = [];
    if (devicesWithoutTestIntervals.length > 0) {
      var nonSupportNameDevices = devicesWithoutTestIntervals.filter(c => c.configNameText !== "Support1" && c.configNameText !== "Support2");
      var supportNameDevices = devicesWithoutTestIntervals.filter(d => d.configNameText == "Support1" || d.configNameText == "Support2");

      var nonSupportNames = nonSupportNameDevices.map(c => c.configNameText);
      var supportNames = supportNameDevices.map(c => c.configNameText);

      if (nonSupportNames.length > 0) {
        var uniqueConfigNames = [... new Set(nonSupportNames)];
          messages.push(new Message(
            `${uniqueConfigNames}`,
            'error',
            nonSupportNameDevices.map(c => <MessageItem>{ id: c.deviceId, text: c.configNameText }),
            MessageTemplateType.EnsureTestInterval
          ));
      }

      if (supportNames.length > 0) {
        var uniqueSupportNames = [... new Set(supportNames)];
        messages.push(new Message(
            `${uniqueSupportNames}`,
            'warning',
            supportNameDevices.map(c => <MessageItem>{ id: c.deviceId, text: c.configNameText }),
            MessageTemplateType.EnsureTestInterval
        ));
      }
    }
    return messages;
  }

  private ensureOverhaulAndProofTestCoverage() {
    // Find devices with proof test coverage less than 100 but having an overhaul interval of zero
    const devices = this.pts.filter(pt => isDevice(pt) && pt.proofTestingCoverage < 1 && !pt.overhaulInterval && !isManual(pt));
    if (devices.length > 0) {
      const configNames = [... new Set(devices.map(d => getDisplayText(d.ptType, d.slot)))];
      return [new Message(
        `${configNames}`,
        'error',
        devices.map(c => <MessageItem>{ id: c.protectiveFunctionID, text: getDisplayText(c.ptType, c.slot) }),
        MessageTemplateType.EnsureOverhaulAndProofTestCoverage
      )]
    }
    return [];
  }

  private ensureProofTestCoverageMinMaxValue() {
    // Find devices with proof test coverage less than 0 or greater than 1
    const devices = this.pts.filter(pt => isDevice(pt) && (pt.proofTestingCoverage < 0 || pt.proofTestingCoverage > 1) && (pt.isInput || pt.isAction));
    if (devices.length > 0) {
      const configNames = [... new Set(devices.map(d => getDisplayText(d.ptType, d.slot)))];
      return [new Message(
        `${configNames}`,
        'error',
        devices.map(c => <MessageItem>{ id: c.protectiveFunctionID, text: getDisplayText(c.ptType, c.slot) }),
        MessageTemplateType.EnsureProofTestCoverageMinMaxValue
      )]
    }
    return [];
  }

  private ensureNoBlankChannels(): Message[] {
    const emptyColumns: any[] = [];
    for (let config of this.allConfigs) {
      var numColumns = config.numColumns;
      var childDevices = this.pts.filter(pt => config.hasChildDevice(pt));
      var columns = childDevices.map(cd => getColumn(cd));
      if (numColumns > 0) {
        const numDistinctColumns = [...new Set(columns)].length;
        if (numDistinctColumns < numColumns) {
          if (config.isMooNConfig && numDistinctColumns != 1)
            emptyColumns.push({ columns: config.getEmptyColumns(childDevices), configName: config.displayText });
          else if (!config.isMooNConfig)
            emptyColumns.push({ columns: config.getEmptyColumns(childDevices), configName: config.displayText });         
        }
      }
    }
    
    if (emptyColumns.length > 0) {
      return [new Message(
        `${emptyColumns.map(x => x.configName).join(', ')}`,
        'error',
        emptyColumns.map(c => <MessageItem>{ id: c.configName, text: c.columns.join(',') }),
        MessageTemplateType.EnsureNoBlankChannels
      )];
    }
    return [];
  }

  // TODO: Finish this
  checkInputsAndActionsConfigsHasDevices(): Message[] {
    const inputConfigs = this.inputConfigs.filter(c => c.hasConfig);
    const actionConfigs = this.actionConfigs.filter(c => c.hasConfig);
    var missingInputs: ConfigSlotWrapper[] = [];
    var missingActions: ConfigSlotWrapper[] = [];
    for (var config of inputConfigs) {
      if (this.deviceData.findIndex(dd => dd.ptProtectiveFunction.slot === config.slot && dd.ptProtectiveFunction.ptType == PtType.InputDevice) == -1) {
        missingInputs.push(config);
      }
    }

    for (var config of actionConfigs) {
      if (this.deviceData.findIndex(dd => dd.ptProtectiveFunction.slot === config.slot && dd.ptProtectiveFunction.ptType == PtType.ActionDevice) == -1) {
        missingActions.push(config);
      }
    }

    if (missingInputs.length !== 0 && missingActions.length !== 0) {
      return [new Message(
        "Missing input and action subsystems.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    } else if (missingInputs.length !== 0) {
      return [new Message(
        "Missing input subsystem.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    } else if (missingActions.length !== 0) {
      return [new Message(
        "Missing action subsystem.",
        'warning',
        [],
        MessageTemplateType.Custom
      )];
    }
    return [];
  }
}